import React, { useMemo } from "react";
import {
  useJsApiLoader,
  GoogleMap,
  Polygon,
  useGoogleMap,
} from "@react-google-maps/api";
import { parsePolygonToPaths, LatLng } from "../../utils/polygonUtils.ts";

const DEFAULT_CENTER = { lat: 39.8283, lng: -98.5795 }; // US center
const DEFAULT_ZOOM = 4;
const DEFAULT_MAP_CONTAINER_HEIGHT_PX = 400;

const defaultMapContainerStyle = (height: string) => ({
  height,
  width: "100%" as const,
});

const getMapOptions = () => ({
  mapTypeId: "satellite" as const,
  mapTypeControl: true,
  mapTypeControlOptions: {
    mapTypeIds: ["satellite", "roadmap"],
  },
  streetViewControl: false,
  fullscreenControl: true,
  zoomControl: false,
  tilt: 0,
  heading: 0,
});

interface FitBoundsProps {
  paths: LatLng[][];
}

const FitBounds: React.FC<FitBoundsProps> = ({ paths }) => {
  const map = useGoogleMap();

  React.useEffect(() => {
    if (!map || paths.length === 0) return;

    const bounds = new google.maps.LatLngBounds();
    paths.forEach((path) => {
      path.forEach((p) => bounds.extend(p));
    });
    map.fitBounds(bounds, 40);
  }, [map, paths]);

  return null;
};

interface PolygonMapProps {
  /** Polygon data as WKT or GeoJSON string */
  polygonData: string;
  /** Optional site name for display */
  siteName?: string;
  /** Optional initial center when no polygon (e.g. from site lat/lng) */
  initialCenter?: { lat: number; lng: number };
  /** Map viewport height (default 400px) */
  mapContainerHeight?: string;
}

const PolygonMap: React.FC<PolygonMapProps> = ({
  polygonData,
  siteName,
  initialCenter = DEFAULT_CENTER,
  mapContainerHeight = `${DEFAULT_MAP_CONTAINER_HEIGHT_PX}px`,
}) => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? "";
  const mapContainerStyle = defaultMapContainerStyle(mapContainerHeight);

  const { paths, error } = useMemo(
    () => parsePolygonToPaths(polygonData),
    [polygonData]
  );

  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: apiKey || "",
  });

  if (!apiKey) {
    return (
      <div
        style={{
          ...mapContainerStyle,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f5f5f5",
          color: "#666",
          flexDirection: "column",
          gap: 8,
        }}
      >
        <span style={{ fontSize: 12 }}>
          Enable Maps JavaScript API 
        </span>
      </div>
    );
  }

  const hasValidPolygon = paths.length > 0;

  if (loadError) {
    return (
      <div
        style={{
          ...mapContainerStyle,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f5f5f5",
          color: "#666",
        }}
      >
        Failed to load Google Maps.
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div
        style={{
          ...mapContainerStyle,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f5f5f5",
          color: "#666",
        }}
      >
        Loading map...
      </div>
    );
  }

  return (
    <div style={{ width: "100%" }}>
      {error && (
        <div
          style={{
            padding: "8px 12px",
            marginBottom: 8,
            backgroundColor: "#fff3cd",
            color: "#856404",
            borderRadius: 4,
            fontSize: 14,
          }}
        >
          {error}
        </div>
      )}
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={hasValidPolygon ? undefined : initialCenter}
        zoom={hasValidPolygon ? undefined : DEFAULT_ZOOM}
        options={getMapOptions()}
      >
        {hasValidPolygon && <FitBounds paths={paths} />}
        {paths.map((path, i) => (
          <Polygon
            key={i}
            path={path}
            options={{
              fillColor: "#65FE08",
              fillOpacity: 0.3,
              strokeColor: "#65FE08",
              strokeWeight: 2,
            }}
          />
        ))}
      </GoogleMap>
      {siteName && hasValidPolygon && (
        <div style={{ marginTop: 8, fontSize: 14, color: "#666" }}>
          {siteName}
        </div>
      )}
    </div>
  );
};

export default PolygonMap;
